import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { CeHelpButton } from "../../generic/components/ce-help-button";
import { CeCheckbox } from "../../generic/components/form-fields/ce-checkbox";
import { RemoveConfirmationDialog } from "./remove-confirmation-dialog";

import type { BrandKitColor } from "../server/brand-kit-service";
import type { BrandKitManager } from "../types";
import type { FC, ReactNode } from "react";

interface BrandKitColorPickerFormData {
  hex: string;
  tag: string;
  is_default_primary_color: boolean;
}

export const BrandKitColorPicker: FC<{
  open: boolean;
  handleClose: () => void;
  onChange: BrandKitManager["color"]["add"];
  existingColor?: BrandKitColor;
  onEdit?: (logo: BrandKitColor) => void;
  onDelete?: () => void;
}> = ({ open, handleClose, existingColor, onChange, onEdit, onDelete }) => {
  const methods = useForm<BrandKitColorPickerFormData>({
    defaultValues: {
      hex:
        existingColor?.hex === "transparent"
          ? existingColor.hex
          : (existingColor?.hex.slice(1) ?? ""),
      tag: existingColor?.tag ?? "",
      is_default_primary_color: existingColor?.is_default_primary_color ?? false,
    },
  });

  const { control, handleSubmit, reset, setValue, watch } = methods;
  // oxlint-disable-next-line react/incompatible-library -- react-hook-form's watch() is not memoizable; nothing to fix on our side
  const watchedHex = watch("hex");

  const handleHexChange = (color: string): void => {
    setValue("hex", color.slice(1));
  };

  const removeDialog = useDialog();

  // Reset form values when existingColor changes
  useEffect(() => {
    if (existingColor) {
      reset({
        hex: existingColor.hex === "transparent" ? existingColor.hex : existingColor.hex.slice(1),
        tag: existingColor.tag ?? "",
        is_default_primary_color: existingColor.is_default_primary_color ?? false,
      });
    } else {
      reset({
        hex: "",
        tag: "",
        is_default_primary_color: false,
      });
    }
  }, [existingColor, reset]);

  const handleDialogClose = (): void => {
    handleClose();
    reset();
  };

  const onSubmit = (data: BrandKitColorPickerFormData): void => {
    if (data.hex) {
      data.hex = data.hex === "transparent" ? data.hex : `#${data.hex}`;
      if (existingColor && onEdit) {
        onEdit({ ...existingColor, ...data });
      } else {
        onChange?.(data);
      }
      handleClose();
      reset();
    }
  };

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (/^([0-9a-f]{0,6}|(t(r(a(n(s(p(a(r(e(n(t)?)?)?)?)?)?)?)?)?)?))$/i.exec(newValue))
        setValue("hex", newValue);
    },
    [setValue],
  );

  const handleDelete = useCallback(() => {
    if (existingColor && onDelete) {
      removeDialog.handleOpen();
    }
    reset();
  }, [existingColor, onDelete, reset, removeDialog]);

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle>{existingColor ? "Edit Color" : "Add Color"}</DialogTitle>
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(onSubmit)(e);
            }}
          >
            <DialogContent>
              <Stack sx={{ gap: 2, justifyContent: "center", alignItems: "center", mb: 2 }}>
                <HexColorPicker color={watchedHex} onChange={handleHexChange} />
                {existingColor && (
                  <Button
                    onClick={handleDelete}
                    variant="outlined"
                    size="small"
                    color="error"
                    data-analytics-id="delete-color"
                  >
                    Delete
                  </Button>
                )}
              </Stack>
              <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
                <Stack direction="row" sx={{ gap: 1 }}>
                  <OutlinedInput
                    inputProps={{ "aria-label": "hex" }}
                    value={watchedHex}
                    startAdornment={
                      /^(t(r(a(n(s(p(a(r(e(n(t)?)?)?)?)?)?)?)?)?)?)$/.exec(watchedHex) ? null : "#"
                    }
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <HelpTooltip>The exact hex code of your color selection</HelpTooltip>
                </Stack>
                <Stack direction="row" sx={{ gap: 1, width: "100%" }}>
                  <Controller
                    name="tag"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="brand-kit-color-picker-tag"
                        label="Brand tag"
                        placeholder="An optional tag to categorise your logo"
                        fullWidth
                      />
                    )}
                  />
                  <HelpTooltip>
                    Add a tag to help you find the logo when creating designs or formatting emails
                  </HelpTooltip>
                </Stack>
                <Box>
                  <CeCheckbox<BrandKitColorPickerFormData>
                    name="is_default_primary_color"
                    analyticsCategory="brandkit"
                    label="Make default"
                  />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Stack direction="row" spacing={1}>
                <Button
                  data-analytics-id="brand-kit-color-picker-cancel"
                  variant="outlined"
                  onClick={handleDialogClose}
                >
                  Cancel
                </Button>
                <Button
                  data-analytics-id="brand-kit-color-picker-ok"
                  variant="contained"
                  type="submit"
                  disabled={!watchedHex}
                >
                  OK
                </Button>
              </Stack>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      {onDelete && (
        <RemoveConfirmationDialog
          type="color"
          open={removeDialog.open}
          onClose={removeDialog.handleClose}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

const HelpTooltip: FC<{ children: ReactNode }> = ({ children }) => (
  <CeHelpButton
    variant="tooltip"
    content={
      <Stack sx={{ p: 2, gap: 1, maxWidth: "300px" }}>
        <Typography variant="caption">{children}</Typography>
      </Stack>
    }
  />
);
